import store from '@/store';
import axios from 'axios';
import router from '@/router';
import { Module, VuexModule, Mutation, Action } from 'vuex-module-decorators';
import {Mutations, Actions, Endpoints, Views, ViewModes} from '@/ts/store/Constants';
import IdentityData from '@/ts/IdentityData';
import ErrorResponse from '@/ts/ErrorResponse';
import { LocalizationHelpers } from '@/ts/helpers/LocalizationHelpers';
import {
    IdentitiesResponse,
    FetchIdentitiesParams,
    SelectIdentityParams,
    IdentityNotificationsResponse,
    IdentitySocketInitParams,
    IdentitySocketResponse,
} from '@/ts/interfaces/IdentityInterfaces';

import { Connection } from '@/configuration/Connection';
import PersonData from '@/ts/PersonData';
import Bus from '@/plugins/event-bus';

import { Device } from '@capacitor/device';

@Module({name: 'Identities', namespaced: true})
export default class Identities extends VuexModule {
    public identities: IdentityData[] = [];
    public selectedIdentity: IdentityData = null;
    public eulaAcceptedAt: string | null  = Date();

    @Mutation
    public RESET_IDENTITIES_STORE_STATE() {
        this.identities       = [];
        this.selectedIdentity = null;
    }

    get getSelectedIdentity(): IdentityData {
        return this.selectedIdentity;
    }

    get getIdentities(): IdentityData[]  {
        return this.identities;
    }

    get getEulaAcceptedAt() {
        return this.eulaAcceptedAt;
    }

    @Mutation
    public SET_EULA_ACCEPTED_AT(value: string | null ) {
        this.eulaAcceptedAt = value;
    }

    @Action
    public ACCEPT_EULA() {
        return axios.patch(Endpoints.Users.EULA_ENDPOINT)
            .then(() => {
                store.commit(Mutations.SET_EULA_ACCEPTED_AT, Date.now);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                    store.dispatch(Actions.LOG_OUT);

                } else {
                    console.log(error);
                }
            });
    }


    @Action
    public FETCH_IDENTITIES(params: FetchIdentitiesParams) {
        axios.get(Endpoints.Identities.IDENTITIES)
            .then((response) => {
                store.commit(Mutations.SET_IDENTITIES, response.data);
                if (params.fromLoginInterface) {
                    if (response.data.length > 1) {
                        return router.push({ name: params.redirectTo });
                    }
                    store.dispatch(Actions.SELECT_IDENTITY, {
                        identity: new IdentityData(
                            response.data[0].parent,
                            response.data[0].context,
                            response.data[0].logo,
                            response.data[0].name,
                            response.data[0].role,
                            response.data[0].userType,
                        ),
                        vue: params.vue,
                    });
                }
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public FETCH_IDENTITIES_NOTIFICATIONS() {
        axios.get(Endpoints.Identities.IDENTITIES_NOTIFICATIONS)
            .then((response) => {
                store.commit(Mutations.SET_IDENTITIES_NOTIFICATIONS, response.data);
            })
            .catch((error) => {
                if (error.response) {
                    const errorResponse = new ErrorResponse(error.response.status, error.response.data.message);
                    store.commit(Mutations.ADD_ERROR_RESPONSE, errorResponse);
                } else {
                    console.log(error);
                }
            });
    }

    @Action
    public SELECT_IDENTITY(data: SelectIdentityParams) {
        return store.dispatch(Actions.SET_IDENTITY, data.identity)
            .then(() => {
                axios.defaults.headers.common['Hococo-Context']   = data.identity.context;
                axios.defaults.headers.common['Hococo-User-Type'] = data.identity.userType;

                return store.dispatch(Actions.FETCH_IDENTITIES_NOTIFICATIONS);
            }).then((response) => {
                return axios.get(Endpoints.Users.USER_ENDPOINT);
            })
            .then((response) => {
                store.state.User.responseCode = response.status;
                store.state.User.sandbox = response.data.sandbox;
                store.commit(Mutations.SET_EULA_ACCEPTED_AT, response.data.eula_accepted_at);

                const abuseActionDictionary = {
                    block: {
                        user: [],
                    },
                    hide: {
                        conversation: [],
                        message: [],
                    },
                    report: {
                        conversation: [],
                        message: [],
                    },
                };

                if (response.data.abuse_actions) {
                    for (const abuseAction of response.data.abuse_actions) {
                        if (!abuseActionDictionary[abuseAction.action_type][abuseAction.content_type].includes(abuseAction.content_id)) {
                            abuseActionDictionary[abuseAction.action_type][abuseAction.content_type].push(abuseAction.content_id);
                        }
                    }
                }

                store.commit(Mutations.SET_ABUSE_ACTIONS_DICTIONARY, abuseActionDictionary);

                const user = new PersonData(
                    response.data.id,
                    response.data.person.first_name,
                    response.data.person.family_name,
                    response.data.person.phone_number,
                    response.data.address ? response.data.address.street : '',
                    response.data.address ? response.data.address.zip : '',
                    response.data.address ? response.data.address.city : '',
                    response.data.email,
                    response.data.property_id,
                    response.data.corporation_id,
                    response.data.isTenant,
                    response.data.mainRole,
                    response.data.hococoStaff,
                );

                if (response.data.features.length > 0) {
                    user.setFeatures(response.data.features);
                }

                user.hasPin = response.data.hasPin;

                user.setProfileImage(response.data.profileImage);
                user.setProfileImageThumbnail(response.data.profileImageThumbnail);

                user.setResidentPropertyData(response.data.propertyData);

                store.state.User.propertyVariantId = response.data.variantId;

                if (response.data.propertyRoleId !== null) {
                    const roleId = response.data.propertyRoleId;
                    user.role = store.state.User.userRoles[store.state.User.userRolesMap[roleId]];
                }

                store.commit(Mutations.UPDATE_PERSON_DATA, user);
                store.commit(Mutations.SET_CORPORATION_SQUARE_LOGO_URL, response.data.corporationId.secondary_logo_cdn);

                return Promise.resolve(user);
            })
            .then(() => {
                store.dispatch(Actions.FETCH_CORPORATIONS);
                store.dispatch(Actions.FETCH_ADMIN_CURRENT_CORPORATION);
                store.dispatch(Actions.FETCH_PROJECTS);
                store.dispatch(Actions.FETCH_BUILDINGS);
                store.dispatch(Actions.FETCH_VARIANTS);
                store.dispatch(Actions.FETCH_PROPERTIES);

                if (data.identity.userType === 'tenant') {
                    store.dispatch(Actions.FETCH_DASHBOARD_ITEMS_PER_PAGE_COUNT);
                    store.dispatch(Actions.FETCH_INQUIRY_TYPES);
                    store.dispatch(Actions.FETCH_INQUIRY_CATEGORIES);
                    store.dispatch(Actions.FETCH_INQUIRIES);
                } else {
                    store.dispatch(Actions.FETCH_PROJECT_CONFIGS);
                }

                store.commit(Mutations.LOGGED_IN, true);
                store.commit(Mutations.SET_LOGGING_IN, false);

                if (store.getters['Viewport/mobileLayout'] === true) {
                    if (router.currentRoute.name === 'dashboard') {
                        Bus.$emit('IdentityChangedMobile', data.identity.userType);
                    } else {
                        router.push('/user/dashboard');
                    }
                } else {
                    if (router.currentRoute.name === 'admin-projects') {
                        Bus.$emit('IdentityChangedDesktop');
                    } else {
                        router.push('/admin/projects');
                    }
                }

                const languageKey = store.state.User.selectedLanguage ? store.state.User.selectedLanguage : store.state.User.corporationDefaultLanguage;

                return languageKey;
            }).then((languageKey) => {
                store.dispatch(Actions.SET_LANGUAGE, {language: languageKey, vue: data.vue});
                store.dispatch(Actions.FETCH_CONVERSATIONS, {vue: data.vue, forceUpdate: true});
                store.dispatch(Actions.FETCH_FEEDBACK_EVENTS);

                if (store.state.Viewport.deviceInfo.platform !== 'web') {
                    store.dispatch(Actions.INITIALIZE_FIREBASE);
                }

                if (store.state.User.personData.hasPin === false) {
                    store.commit(Mutations.SHOW_SET_PIN, true);
                }

                store.dispatch(Actions.INIT_IDENTITIES_SOCKET, {userId: store.state.User.personData.id, vue: data.vue});
            }).catch((error) => {
                if (error.response && error.response.status !== null) {
                    store.state.User.responseCode = error.response.status;
                    store.commit(Mutations.SET_VIEW, {
                        view: Views.LogIn,
                        viewMode: ViewModes.LogIn.ErrorView,
                    });
                } else {
                    store.state.User.responseCode = null;
                }

                store.commit(Mutations.SET_LOGGING_IN, false);
            });
    }

    @Action
    public SWITCH_IDENTITY(data: SelectIdentityParams) {
        store.commit(Mutations.RESET_ABUSE_STORE_STATE);                    // Abuse,
        store.commit(Mutations.RESET_MESSAGES_STORE_STATE);                 // Messages,
        store.commit(Mutations.RESET_CAMERA_STORE_STATE);                   // Camera,
        store.commit(Mutations.RESET_IMAGES_STORE_STATE);                   // Images,
        store.commit(Mutations.RESET_COMMENTS_STORE_STATE);                 // Comments,
        store.commit(Mutations.RESET_NOTIFICATIONS_STORE_STATE);            // Notifications,
        store.commit(Mutations.RESET_EVENTS_STORE_STATE);                   // Events,
        store.commit(Mutations.RESET_DOCUMENTS_STORE_STATE);                // Documents,
        store.commit(Mutations.RESET_OPTIONS_STORE_STATE);                  // Options,
        store.commit(Mutations.RESET_FEEDBACK_STORE_STATE);                 // Feedback,
        store.commit(Mutations.RESET_BUILDINGS_STORE_STATE);                // Buildings,
        store.commit(Mutations.RESET_CORPORATIONS_STORE_STATE);             // Corporations,
        store.commit(Mutations.RESET_EDITOR_STORE_STATE);                   // Editor,
        store.commit(Mutations.RESET_PROJECTS_STORE_STATE);                 // Projects,
        store.commit(Mutations.RESET_PROPERTIES_STORE_STATE);               // Properties,
        store.commit(Mutations.RESET_VARIANTS_STORE_STATE);                 // Variants,
        store.commit(Mutations.RESET_LIKES_STORE_STATE);                    // Likes,
        store.commit(Mutations.RESET_HOME_ASSISTANT_STORE_STATE);           // HomeAssistant,
        store.commit(Mutations.RESET_ISSUE_REPORT_STORE_STATE);             // IssueReport,
        store.commit(Mutations.RESET_USER_TICKETS_ATTRIBUTES_STORE_STATE);  // TicketAttributes,
        store.commit(Mutations.RESET_LOADING_STORE_STATE);                  // Loading,
        store.commit(Mutations.RESET_ATTACHMENTS_STORE_STATE);              // Attachments,
        store.commit(Mutations.RESET_TAGS_STORE_STATE);                     // Tags,
        store.commit(Mutations.RESET_CATEGORIES_STORE_STATE);               // Categories,
        store.commit(Mutations.RESET_SOCKET_INITIALIZATIONS);               // Socket,
        store.commit(Mutations.RESET_USER_MANAGEMENT_STORE_STATE);          // UserManagement,
        store.commit(Mutations.RESET_TICKETS_STORE_STATE);                  // Tickets,
        store.commit(Mutations.RESET_WAITING_LIST_STORE_STATE);             // WaitingLists,
        store.commit(Mutations.RESET_EVENT_LOGS_STORE_STATE);               // EventLogs,
        store.commit(Mutations.RESET_USER_TICKETS_STORE_STATE);             // UserTickets,
        store.commit(Mutations.RESET_PRESENTATION_FLOWS_STORE_STATE);       // PresentationFlows,
        store.commit(Mutations.RESET_INTEGRATIONS_STORE_STATE);             // Integrations,
        store.commit(Mutations.SET_USER_PROFILES_STORE_STATE);              // UserProfiles,
        store.commit(Mutations.RESET_CONFIRM_POPUP_STORE_STATE);            // ConfirmPopup,
        store.commit(Mutations.RESET_STAFF_PROPERTIES_STORE_STATE);         // StaffProperties,
        store.commit(Mutations.RESET_SHARED_FACILITIES_STORE_STATE);        // SharedFacilities,
        store.commit(Mutations.RESET_ROLES_AND_PERMISSIONS);                // RolesAndPermissions,
        store.commit(Mutations.RESET_RECIPIENTS);                           // Recipients,
        store.commit(Mutations.RESET_PROPERTY_NOTES_STATE);                 // PropertyNotes,
        store.commit(Mutations.RESET_GROUPINGS_STORE_STATE);                // Groupings,
        store.commit(Mutations.RESET_UNIT_GROUPING_CHARTS_STORE_STATE);     // UnitGroupingCharts,
        store.commit(Mutations.RESET_TEMPLATE_RENDERER_STORE_STATE);        // TemplateRenderer,
        store.commit(Mutations.RESET_FILE_UPLOAD_STORE_STATE);              // FileUpload,
        store.commit(Mutations.RESET_OVERLAYS_STORE_STATE);                 // Overlays,
        store.commit(Mutations.RESET_GLOBAL_OVERLAY_STORE_STATE);           // Global Overlays,
        store.commit(Mutations.RESET_SCROLL_STORE_STATE);                   // Scroll,
        store.commit(Mutations.RESET_TABLE_CONFIG_STORE_STATE);             // Table Config,
        store.commit(Mutations.RESET_DASHBOARD_STORE_STATE);                // User Notifications,

        if (Connection.socketEnabled) {
            data.vue.$echo.disconnect();
            data.vue.$echo.connector.socket.connect();
        }

        store.dispatch(Actions.SELECT_IDENTITY, data);
    }

    @Action
    public SET_IDENTITY(identity: IdentityData) {
        store.commit(Mutations.SET_USER_IDENTITY, identity);
        return Promise.resolve(this.selectedIdentity);
    }

    @Action
    public INIT_IDENTITIES_SOCKET(params: IdentitySocketInitParams) {
        if (!Connection.socketEnabled) {
            return;
        }

        params.vue.$echo.private(`user.${params.userId}.event-log`)
            .listen(('.event-log.new'), (payload: any) => {
                store.commit(Mutations.UPDATE_IDENTITY_NOTIFICATIONS, { corporationId: payload.corporation_id });
            });
    }

    @Mutation
    public SET_USER_IDENTITY(identity: IdentityData) {
        this.selectedIdentity = identity;
    }

    @Mutation
    public SET_IDENTITIES(identities: IdentitiesResponse[]) {
        this.identities = [];

        for (const identity of identities) {
            this.identities.push(new IdentityData(
                identity.parent,
                identity.context,
                identity.logo,
                identity.name,
                identity.role,
                identity.userType,
            ));
        }
    }

    @Mutation
    public UPDATE_IDENTITY_NOTIFICATIONS(identityPayload: IdentitySocketResponse) {
        const identity = this.identities.find((identityObj: IdentityData) => identityObj.parent === identityPayload.corporationId);

        if (identity) {
            identity.incrementNotifications();
        }
    }

    @Mutation
    public SET_IDENTITIES_NOTIFICATIONS(identitiesPayload: IdentityNotificationsResponse[]) {
        for (const identity of identitiesPayload) {
            const targetedIdentity = this.identities.find((identityObj: IdentityData) => identityObj.isTheSameIdentity(identity));

            if (targetedIdentity) {
                targetedIdentity.updateNotifications(identity.notificationsCount);
            }
        }
    }
}
